<main class="p-3 p-lg-4 p-xxl-5">
  <div v-if="!has_selected_case">
    <section class="card border rounded-3 overflow-hidden">
      <div class="card-header bg-white d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
        <div>
          <div class="text-uppercase text-muted fw-semibold small mb-1">E-signing queue</div>
          <div class="text-muted small mb-0">Open an application to track signing activity and send signing requests.</div>
        </div>

        <label class="onboarding-queue-search d-inline-flex align-items-center">
          <i class="bi bi-search" aria-hidden="true"></i>
          <input v-model.trim="case_table_search" type="search" placeholder="Search applications">
        </label>
      </div>

      <div v-if="cases_error" class="alert alert-warning border-0 rounded-0 mb-0 px-4 py-3">
        {{ cases_error }}
      </div>

      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th class="text-uppercase text-muted small fw-semibold py-3 ps-4">Applicant</th>
              <th class="text-uppercase text-muted small fw-semibold py-3">Journey</th>
              <th class="text-uppercase text-muted small fw-semibold py-3">Current status</th>
              <th class="text-uppercase text-muted small fw-semibold py-3">Owner</th>
              <th class="text-uppercase text-muted small fw-semibold py-3 pe-4 text-end">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading_cases">
              <td colspan="5" class="px-4 py-5 text-center text-muted">
                Loading e-signing applications...
              </td>
            </tr>
            <tr v-else-if="!filtered_gate_cases.length">
              <td colspan="5" class="px-4 py-5 text-center text-muted">
                No applications are currently in the e-signing gate.
              </td>
            </tr>
            <tr
              v-else
              v-for="case_item in filtered_gate_cases"
              :key="case_item.application_id || case_item.id"
              class="cursor-pointer"
              tabindex="0"
              @click="open_case_summary(case_item)"
              @keyup.enter="open_case_summary(case_item)"
            >
              <td class="ps-4 py-3">
                <div class="fw-bold text-black">{{ case_item.name || 'Onboarding case' }}</div>
                <div class="text-muted small">{{ case_item.detail || case_item.company_name || '' }}</div>
              </td>
              <td class="py-3">{{ case_item.journey || 'Onboarding' }}</td>
              <td class="py-3">
                <span class="badge rounded-pill bg-warning-subtle text-warning-emphasis fw-normal">
                  {{ case_item.workflow_display_stage || case_item.current_workflow_status || case_item.stage || 'E-signing' }}
                </span>
              </td>
              <td class="py-3">{{ case_item.owner || case_item.onboarding_owner_role_name || 'Unassigned' }}</td>
              <td class="pe-4 py-3 text-end">
                <button
                  type="button"
                  class="onboarding-queue-view-button"
                  @click="open_case_detail(case_item, $event)"
                  :disabled="opening_case_id === Number(case_item.application_id || case_item.id || 0)"
                >
                  <span>{{ opening_case_id === Number(case_item.application_id || case_item.id || 0) ? 'Opening...' : 'View' }}</span>
                  <i class="bi bi-arrow-right-short" aria-hidden="true"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div
      v-if="selected_case_summary"
      class="onboarding-case-detail-backdrop"
      @click="close_case_summary"
    ></div>

    <aside
      v-if="selected_case_summary"
      :class="['onboarding-case-detail-drawer bg-white shadow-lg', { 'is-fullscreen': is_summary_detail_fullscreen }]"
    >
      <header class="border-bottom p-4">
        <div class="d-flex align-items-start justify-content-between gap-3">
          <div class="min-w-0">
            <div class="text-uppercase text-muted fw-semibold small mb-3">
              {{ selected_case_summary.journey || 'Onboarding' }} onboarding · E-signing gate
            </div>
            <h2 class="h5 fw-bold text-black mb-2">{{ selected_case_summary.name || 'Onboarding case' }}</h2>
            <p class="mb-0 text-muted">{{ selected_case_summary_subtitle() }}</p>
          </div>

          <div class="d-flex align-items-center gap-3 flex-shrink-0">
            <span class="badge rounded-pill bg-warning-subtle text-warning-emphasis fw-normal">
              <span class="me-1">&bull;</span>{{ selected_case_summary.activation || selected_case_summary.stage || 'E-signing' }}
            </span>
            <button
              type="button"
              class="btn btn-link text-muted p-0"
              :aria-label="is_summary_detail_fullscreen ? 'Exit fullscreen' : 'View fullscreen'"
              :title="is_summary_detail_fullscreen ? 'Exit fullscreen' : 'View fullscreen'"
              @click="toggle_summary_detail_fullscreen"
            >
              <i :class="['bi', is_summary_detail_fullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen', 'fs-5']"></i>
            </button>
            <button type="button" class="btn btn-link text-muted p-0" aria-label="Close entity detail" @click="close_case_summary">
              <i class="bi bi-x-lg fs-5"></i>
            </button>
          </div>
        </div>
      </header>

      <div class="onboarding-case-detail-body p-4">
        <nav class="bg-light rounded-3 p-1 d-flex align-items-center gap-2 mb-4" aria-label="E-signing detail sections">
          <button
            v-for="tab in summary_detail_tabs"
            :key="tab.id"
            type="button"
            :class="[
              'btn btn-sm flex-fill d-inline-flex align-items-center justify-content-center gap-2',
              active_summary_detail_tab === tab.id ? 'bg-white border shadow-sm fw-semibold' : 'border-0 bg-transparent'
            ]"
            @click="set_summary_detail_tab(tab.id)"
          >
            <i :class="['bi', tab.icon]"></i>
            <span>{{ tab.label }}</span>
            <span v-if="tab.count" class="badge rounded-pill text-bg-danger">{{ tab.count }}</span>
          </button>
        </nav>
        <section v-if="active_summary_detail_tab === 'summary'">
          <div v-if="is_loading_summary_detail" class="text-muted mb-4">Loading entity detail...</div>

          <dl class="row g-0 mb-4">
            <template v-for="row in summary_rows" :key="row.label">
              <dt class="col-4 text-muted fw-normal mb-3">{{ row.label }}</dt>
              <dd class="col-8 fw-semibold mb-3">{{ row.value }}</dd>
            </template>
          </dl>

          <section>
            <div class="text-uppercase text-muted fw-semibold small mb-3">Stage timeline</div>
            <div class="onboarding-case-timeline">
              <article
                v-for="item in summary_stage_timeline"
                :key="`${item.title}-${item.detail}`"
                :class="['onboarding-case-timeline-item', `is-${item.status}`]"
              >
                <span class="onboarding-case-timeline-icon">
                  <i :class="['bi', item.icon]"></i>
                </span>
                <div>
                  <div class="fw-semibold text-black">{{ item.title }}</div>
                  <div class="text-muted small mt-1">{{ item.detail }}</div>
                </div>
              </article>
            </div>
          </section>
        </section>

        <section v-else>
          <ApplicationDetailsReview :form_sections="summary_case_form_sections" />
        </section>
      </div>

      <footer class="onboarding-case-detail-footer border-top bg-white p-3 d-flex align-items-center justify-content-between gap-3">
        <button type="button" class="btn btn-link text-muted d-inline-flex align-items-center gap-2 text-decoration-none">
          <i class="bi bi-stars"></i>
          <span>Ask AI</span>
        </button>

        <div class="d-flex align-items-center gap-2">
          <button
            type="button"
            class="btn btn-light border"
            @click="close_case_summary"
          >
            Close
          </button>
          <button
            type="button"
            class="btn onboarding-bg-navy-dark text-white d-inline-flex align-items-center gap-2"
            @click="open_selected_summary_case_detail"
          >
            <i class="bi bi-arrow-right"></i>
            <span>Open e-signing</span>
          </button>
        </div>
      </footer>
    </aside>
  </div>

  <div v-else>
    <section class="mb-3">
      <button type="button" class="btn btn-link text-decoration-none px-0 py-0 d-inline-flex align-items-center gap-2 small" @click="go_back_to_esigning_queue">
        <i class="bi bi-arrow-left small"></i>
        <span>Back to E-signing</span>
      </button>
    </section>

    <section class="card border rounded-3 mb-4">
      <div class="card-body p-3 p-lg-4">
        <div class="workflow-stepper">
          <div v-for="step in case_journey_steps" :key="`${step.gate_id}-${step.label}`" class="workflow-stepper__item">
            <div class="workflow-stepper__content">
              <span
                class="rounded-circle d-inline-flex align-items-center justify-content-center border flex-shrink-0 workflow-step-circle"
                :class="step.state === 'completed' ? 'bg-success border-success text-white' : step.state === 'current' ? 'border-success text-success bg-white' : 'border-light text-muted bg-white'"
              >
                <i v-if="step.state === 'completed'" class="bi bi-check-lg"></i>
                <span v-else>{{ step.step_number }}</span>
              </span>
              <div class="workflow-stepper__label fw-semibold text-black">{{ step.label }}</div>
              <div class="workflow-stepper__meta text-muted small text-capitalize">{{ step.state }}</div>
            </div>
            <span v-if="step !== case_journey_steps[case_journey_steps.length - 1]" class="text-muted">→</span>
          </div>
        </div>
      </div>
    </section>

    <section class="gate-overview-card mb-4">
      <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap">
        <div class="d-flex align-items-center gap-3 min-w-0">
          <span class="gate-overview-card__avatar config-navbar-initials rounded-circle d-inline-flex align-items-center justify-content-center fw-bold">
            {{ selected_case_name?.charAt(0) || 'E' }}
          </span>
          <div class="min-w-0">
            <div class="gate-overview-card__eyebrow">Applicant e-signing review</div>
            <h2 class="h5 fw-bold text-black mb-1">{{ selected_case_name }}</h2>
            <p class="gate-overview-card__meta mb-0">{{ selected_case_subtitle }}</p>
          </div>
        </div>

        <span class="badge rounded-pill bg-warning-subtle text-warning-emphasis fw-normal px-3 py-2 d-none">
          {{ selected_case_status }}
        </span>
      </div>
    </section>

    <section class="card border rounded-3 mb-4 overflow-hidden">
      <div class="onboarding-gate-audit-layout">
        <aside class="onboarding-gate-audit-sidebar">
          <section class="onboarding-gate-current-card-wrap" aria-label="Pinned action">
            <div class="text-uppercase text-muted fw-semibold small">Pinned</div>
            <button
              v-if="detail_tabs_with_history[0]"
              type="button"
              :class="['onboarding-gate-current-card', { 'is-active': active_detail_tab === detail_tabs_with_history[0].id }]"
              @click="set_active_detail_tab(detail_tabs_with_history[0].id)"
            >
              <span class="onboarding-gate-current-card__icon" aria-hidden="true"></span>
              <span class="onboarding-gate-current-card__body">
                <span class="onboarding-gate-current-card__title">Required Action</span>
                <span class="onboarding-gate-current-card__copy">{{ current_detail_action_copy() }}</span>
              </span>
              <span class="onboarding-gate-current-card__count">1</span>
            </button>
          </section>

          <button
            v-for="tab in detail_tabs_with_history.filter((item, index) => index > 0 && /application reference/i.test(item.label || ''))"
            :key="`reference-${tab.id}`"
            type="button"
            :class="['onboarding-gate-audit-reference', { 'is-active': active_detail_tab === tab.id }]"
            @click="set_active_detail_tab(tab.id)"
          >
            <span class="onboarding-gate-audit-reference__label">{{ tab.label }}</span>
            <span class="onboarding-gate-audit-reference__value">Open application details</span>
          </button>

          <section class="onboarding-gate-audit-panel" aria-label="Case history">
            <div class="onboarding-gate-audit-panel__eyebrow">Onboarding History</div>
            <div class="onboarding-gate-audit-list">
              <button
                v-for="(tab, index) in detail_tabs_with_history.filter((item, itemIndex) => itemIndex > 0 && !/application reference/i.test(item.label || ''))"
                :key="tab.id"
                type="button"
                :class="['onboarding-gate-audit-item', { 'is-active': active_detail_tab === tab.id, 'is-completed': String(tab.badge || '').trim().toLowerCase() === 'completed', 'is-current': String(tab.badge || '').trim().toLowerCase() === 'current' }]"
                @click="set_active_detail_tab(tab.id)"
              >
                <span class="onboarding-gate-audit-item__marker" aria-hidden="true">
                  <i v-if="String(tab.badge || '').trim().toLowerCase() === 'completed'" class="bi bi-check-lg"></i>
                  <span v-else>{{ index + 1 }}</span>
                </span>
                <span class="onboarding-gate-audit-item__content">
                  <span class="onboarding-gate-audit-item__title">{{ tab.label }}</span>
                  <span v-if="tab.badge" :class="['onboarding-gate-audit-item__status', { 'is-current': String(tab.badge || '').trim().toLowerCase() === 'current' }]">
                    {{ tab.badge }}
                  </span>
                  <span class="onboarding-gate-audit-item__detail">Review this gate section.</span>
                </span>
              </button>
            </div>
          </section>
        </aside>

        <div :class="['onboarding-gate-audit-content p-4', { 'onboarding-gate-audit-content--history': active_detail_shell_tab === 'history' }]">
          <nav class="risk-review-tabs mb-4" aria-label="E-signing workspace sections">
            <button
              v-for="tab in detail_shell_tabs"
              :key="tab.id"
              type="button"
              :class="['risk-review-tabs__tab', { 'is-active': active_detail_shell_tab === tab.id }]"
              @click="set_active_detail_shell_tab(tab.id)"
            >
              <span>{{ tab.label }}</span>
              <span v-if="tab.badge" class="badge rounded-pill">{{ tab.badge }}</span>
            </button>
          </nav>

          <section
            v-if="active_detail_shell_tab === 'action' && !active_historical_gate_tab"
            class="px-4 py-3 mb-2"
            aria-label="Current e-signing action"
          >
            <p class="text-dark mb-0">{{ current_detail_action_copy() }}</p>
          </section>

          <section v-if="active_detail_shell_tab === 'history'" class="verification-manual-info-card mb-4 onboarding-history-rail-section">
            <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
              <div>
                <h2 class="h5 mb-1">Onboarding History</h2>
                <p class="text-muted mb-0">Previous gates completed for this case.</p>
              </div>
            </div>

            <div v-if="detail_history_tabs.length" class="onboarding-gate-audit-list">
              <button
                v-for="tab in detail_history_tabs"
                :key="tab.id"
                type="button"
                :class="['onboarding-gate-audit-item', { 'is-active': active_detail_tab === tab.id, 'is-completed': true }]"
                @click="set_active_detail_tab(tab.id)"
              >
                <span class="onboarding-gate-audit-item__marker" aria-hidden="true">
                  <i class="bi bi-check-lg"></i>
                </span>
                <span class="onboarding-gate-audit-item__content">
                  <span class="onboarding-gate-audit-item__title">{{ tab.label }}</span>
                  <span class="onboarding-gate-audit-item__status">{{ tab.badge }}</span>
                  <span class="onboarding-gate-audit-item__detail">Review this completed gate.</span>
                </span>
              </button>
            </div>
            <div v-else class="text-muted">No previous gates have been completed for this case yet.</div>
          </section>

          <section v-if="active_detail_shell_tab === 'history' && !active_historical_gate_tab" class="verification-manual-info-card onboarding-history-detail-placeholder">
            <div class="text-muted">Select a completed gate to view its details.</div>
          </section>

          <section v-else-if="active_historical_gate_tab">
            <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-4">
              <div>
                <h2 class="h5 mb-1">{{ active_historical_gate_tab.label }}</h2>
                <p class="text-muted mb-0">{{ historical_gate_context_copy(active_historical_gate_tab) }}</p>
              </div>
              <span class="badge rounded-pill text-bg-light border">Completed</span>
            </div>

            <div v-if="!['verification', 'manual-verification'].includes(active_historical_gate_tab.gate_id)" class="row g-3 mb-4">
              <div class="col-md-6 col-xl-3">
                <div class="border rounded-3 px-3 py-2 bg-light h-100">
                  <div class="text-uppercase text-muted fw-semibold small mb-2">Action</div>
                  <div class="fw-semibold text-black">{{ historical_gate_action_label() }}</div>
                </div>
              </div>
              <div class="col-md-6 col-xl-3">
                <div class="border rounded-3 px-3 py-2 bg-light h-100">
                  <div class="text-uppercase text-muted fw-semibold small mb-2">Actioned by</div>
                  <div class="fw-semibold text-black">{{ historical_gate_actor() }}</div>
                </div>
              </div>
              <div class="col-md-6 col-xl-3">
                <div class="border rounded-3 px-3 py-2 bg-light h-100">
                  <div class="text-uppercase text-muted fw-semibold small mb-2">Completed</div>
                  <div class="fw-semibold text-black">{{ historical_gate_completed_at() }}</div>
                </div>
              </div>
              <div class="col-md-6 col-xl-3">
                <div class="border rounded-3 px-3 py-2 bg-light h-100">
                  <div class="text-uppercase text-muted fw-semibold small mb-2">Status</div>
                  <div class="fw-semibold text-black">{{ historical_gate_status_label() }}</div>
                </div>
              </div>
            </div>

            <div v-if="!['verification', 'manual-verification'].includes(active_historical_gate_tab.gate_id)" class="border rounded-3 px-3 py-3 bg-light mb-4">
              <div class="text-uppercase text-muted fw-semibold small mb-2">Reason</div>
              <div class="text-black">{{ historical_gate_reason() }}</div>
            </div>

            <ManualVerificationHistoryView
              v-if="active_historical_gate_tab.gate_id === 'manual-verification'"
            :show-history-tabs="false"
            :decision-action="historical_manual_decision_action()"
            :decision-comment="historical_manual_comment()"
            :decision-label="historical_manual_decision_label()"
            :decision-tone="historical_manual_decision_tone()"
            :status-label="historical_manual_status_label()"
            :review-sections="historical_manual_review_sections()"
            :applicant-form-sections="case_form_sections"
            :documents="selected_case?.documents || []"
          />

          <div v-else-if="active_historical_gate_tab.gate_id === 'verification'">
            <HistoricalVerificationChecksView
              :checks="active_historical_gate_checks"
              empty-message="No verification results were returned for this completed gate."
              :is-expanded="historical_verification_check_expanded"
              :get-check-passed-chips="get_check_passed_chips"
              :get-check-result-detail-rows="get_check_result_detail_rows"
              :get-check-history="get_check_history"
              :status-badge-class="status_badge_class"
              :evidence-icon-class="evidence_icon_class"
              :field-result-row-class="() => ''"
              @toggle-check="toggle_historical_verification_check_expanded"
            />
          </div>

          <div v-else-if="active_historical_gate_tab.gate_id === 'risk-review'">
            <nav class="risk-review-tabs mb-4" aria-label="Risk review sections">
              <button
                v-for="tab in historical_risk_secondary_tabs()"
                :key="tab.id"
                type="button"
                :class="['risk-review-tabs__tab', { 'is-active': tab.active }]"
                disabled
              >
                <span>{{ tab.label }}</span>
              </button>
            </nav>

            <section class="risk-review-layout">
              <aside class="risk-review-panel risk-review-panel--tinted">
                <h2 class="risk-review-section-title">Applicant Weight Score</h2>
                <p class="risk-review-section-copy">This compares the applicant's selected answer weights against the configured maximum weight for this assessment.</p>
                <div class="risk-review-score-card mt-4">
                  <div class="risk-review-score-card__value">
                    {{ historical_risk_score_value() }}
                    <span>/ {{ historical_risk_total_possible_weight() || '-' }}</span>
                  </div>
                  <div class="risk-review-score-card__label">Applicant weighted score</div>
                  <div class="risk-review-score-card__bar" aria-hidden="true">
                    <span :style="{ width: `${historical_risk_score_percent()}%` }"></span>
                  </div>
                  <div class="risk-review-score-card__percent">{{ historical_risk_score_percent() }}% of possible risk weight</div>
                </div>
                <div class="risk-review-stat-grid mt-4">
                  <div class="risk-review-stat">
                    <div class="risk-review-stat__label">Risk level</div>
                    <div class="risk-review-stat__value">{{ historical_risk_summary_value('risk level', 'Not assessed') }}</div>
                  </div>
                  <div class="risk-review-stat">
                    <div class="risk-review-stat__label">Auto decision</div>
                    <div class="risk-review-stat__value">{{ historical_risk_summary_value('auto decision', '-') }}</div>
                  </div>
                  <div class="risk-review-stat">
                    <div class="risk-review-stat__label">Recommended action</div>
                    <div class="risk-review-stat__value">{{ historical_risk_summary_value('reviewer action', '-') }}</div>
                  </div>
                </div>
                <div class="risk-review-decision mt-4">
                  <div class="fw-bold text-black mb-1">Admin decision guide</div>
                  <p class="mb-0">{{ historical_risk_review_guidance() }}</p>
                </div>
              </aside>

              <div class="risk-review-panel">
                <div class="d-flex align-items-center justify-content-between gap-3 mb-4">
                  <div>
                    <h2 class="risk-review-section-title">Applicant Risk Evidence</h2>
                    <p class="risk-review-section-copy">Each applicant answer is compared with all configured weights for that question.</p>
                  </div>
                  <span class="badge rounded-pill bg-light text-dark border">{{ get_historical_risk_factor_items().length }} items</span>
                </div>

                <div v-if="get_historical_risk_factor_items().length" class="risk-review-answer-list">
                  <article
                    v-for="factor in get_historical_risk_factor_items()"
                    :key="factor.id"
                    class="risk-review-answer-card"
                  >
                    <div class="risk-review-answer-card__top">
                      <div class="min-w-0">
                        <div class="risk-review-answer-card__title">{{ factor.label }}</div>
                        <div class="risk-review-answer-card__answer">Applicant answer: {{ factor.selected_answer || '-' }}</div>
                        <div class="risk-review-answer-card__comparison">
                          <span>{{ factor.selected_score || 0 }} selected</span>
                          <span>{{ factor.max_score || 0 }} max</span>
                        </div>
                        <div class="risk-review-answer-card__bar" aria-hidden="true">
                          <span :style="{ width: `${factor.comparison_percent || 0}%` }"></span>
                        </div>
                        <div v-if="factor.choices.length" class="risk-review-weight-options">
                          <span
                            v-for="choice in factor.choices"
                            :key="`${factor.id}-${choice.option}`"
                            :class="['risk-review-weight-option', { 'is-selected': choice.is_selected }]"
                          >
                            {{ choice.option }}: {{ choice.weight }}
                          </span>
                        </div>
                      </div>
                      <span class="risk-review-answer-card__score">{{ factor.selected_score || 0 }}/{{ factor.max_score || 0 }}</span>
                    </div>
                  </article>
                </div>
                <div v-else class="text-muted">No submitted risk answers were returned for this assessment.</div>
              </div>
            </section>
          </div>

          <div v-else>
            <div class="border rounded-3 p-4 bg-light">
              <div class="fw-semibold text-black mb-2">{{ active_historical_gate_tab.label }}</div>
              <div class="text-muted">{{ get_historical_status_message(active_historical_gate_tab) }}</div>
            </div>
          </div>
        </section>

        <section v-else-if="active_detail_tab === 'application'">
          <ApplicationDetailsReview :form_sections="case_form_sections" />
        </section>

        <section v-else>
          <div v-if="!show_new_signing_request">
      <section class="card border rounded-3 mb-4">
        <div class="card-body p-4">
          <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap">
            <div class="d-flex align-items-start gap-3 min-w-0">
              <span
                class="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary flex-shrink-0 onboarding-icon-square-21">
                <i class="bi bi-vector-pen"></i>
              </span>

              <div class="min-w-0">
                <div class="text-uppercase text-muted fw-semibold small mb-1">Signing overview</div>
                <p class="h6 fw-bold text-black mb-2">{{ signing_overview_title }}</p>
                <p class="text-muted mb-0">{{ signing_overview_copy }}</p>
              </div>
            </div>

            <div class="d-flex align-items-center gap-2 flex-wrap">
              <button
                v-if="can_manage_signing_requests"
                type="button"
                @click="open_new_signing_request"
                class="btn btn-primary-dark text-xs d-inline-flex align-items-center gap-2 fw-semibold px-3"
                :disabled="!has_signing_context"
              >
                <i class="bi bi-plus-lg"></i>
                <span>Signing request</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section class="card border rounded-3 overflow-hidden">
        <div class="card-header bg-white d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
          <div class="d-inline-flex align-items-center flex-wrap border rounded-2 bg-light p-1" role="group"
            aria-label="E-signing filters">
            <button
              v-for="filter in signing_filter_options"
              :key="filter.id"
              type="button"
              class="btn btn-sm px-3 py-1"
              :class="active_signing_filter === filter.id ? 'bg-white border shadow-sm fw-semibold' : 'border-0 bg-transparent'"
              @click="set_signing_filter(filter.id)">
              {{ filter.label }}
              <span class="ms-1">{{ filter.count }}</span>
            </button>
          </div>

          <div class="d-flex align-items-center justify-content-end flex-wrap gap-2">
            <label class="onboarding-queue-search d-inline-flex align-items-center">
              <i class="bi bi-search" aria-hidden="true"></i>
              <input v-model.trim="table_search" type="search" placeholder="Search contracts">
            </label>
          </div>
        </div>

        <div v-if="sent_contracts_error" class="alert alert-warning border-0 rounded-0 mb-0 px-4 py-3">
          {{ sent_contracts_error }}
        </div>

        <div class="table-responsive">
          <table class="table align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th class="text-uppercase text-muted small fw-semibold py-3 ps-4">File name</th>
                <th class="text-uppercase text-muted small fw-semibold py-3">Type</th>
                <th class="text-uppercase text-muted small fw-semibold py-3">Sent on</th>
                <th class="text-uppercase text-muted small fw-semibold py-3">Status</th>
                <th class="text-uppercase text-muted small fw-semibold py-3 pe-4 text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading_selected_case || sent_contracts_loading">
                <td colspan="5" class="px-4 py-5 text-center text-muted">
                  Loading sent contracts...
                </td>
              </tr>
              <tr v-else v-for="document in filtered_signing_documents" :key="document.id">
                <td class="ps-4 py-3">
                  <div class="d-flex align-items-start gap-3">
                    <span
                      class="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary flex-shrink-0 onboarding-icon-square-21">
                      <i class="bi bi-file-earmark-text"></i>
                    </span>
                    <div>
                      <div class="fw-bold text-black">{{ document.title }}</div>
                      <div class="text-muted small">{{ document.meta }}</div>
                    </div>
                  </div>
                </td>
                <td class="py-3 text-uppercase">{{ document.type }}</td>
                <td class="py-3">{{ document.sent_on }}</td>
                <td class="py-3">
                  <span :class="['badge rounded-pill fw-normal', document.status_class]">{{ document.status }}</span>
                </td>
                <td class="pe-4 py-3 text-end">
                  <button type="button" class="onboarding-queue-view-button" @click="open_signing_tracker(document)"
                    :disabled="!document.can_track">
                    <span>{{ document.can_track ? 'Track' : 'Unavailable' }}</span>
                    <i class="bi bi-arrow-right-short" aria-hidden="true"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="!loading_selected_case && !sent_contracts_loading && !filtered_signing_documents.length">
                <td colspan="5" class="px-4 py-5 text-center text-muted">
                  No sent contracts match the current filter.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
          </div>

          <div v-else>
      <section class="card border rounded-3 mb-4">
        <div class="card-body p-4">
          <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap">
            <div class="d-flex align-items-start gap-3 min-w-0">
              <span
                class="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary flex-shrink-0 onboarding-icon-square-21">
                <i class="bi bi-vector-pen"></i>
              </span>

              <div class="min-w-0">
                <div class="text-uppercase text-muted fw-semibold small mb-1">Signing overview</div>
                <p class="h6 fw-bold text-black mb-2">{{ signing_overview_title }}</p>
                <p class="text-muted mb-0">{{ signing_overview_copy }}</p>
              </div>
            </div>

            <button
              type="button"
              @click="close_new_signing_request"
              class="btn btn-light border d-inline-flex align-items-center gap-2 fw-semibold px-3"
            >
              <i class="bi bi-x-lg"></i>
              <span>Close request</span>
            </button>
          </div>
        </div>
      </section>

      <section class="mb-3">
        <div class="py-3 px-0">
          <div class="d-flex align-items-center gap-2 gap-lg-3">
            <button type="button"
              class="esigning-square-225 btn btn-sm bg-white border rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
              @click="navigate_to_request_step(current_request_step - 1)"
              :disabled="current_request_step <= 1 || !can_navigate_to_step(current_request_step - 1)">
              <i class="bi bi-chevron-left text-muted"></i>
            </button>

            <div class="d-flex align-items-center flex-grow-1 gap-2 gap-lg-3 overflow-auto">
              <button type="button" class="btn p-0 border-0 bg-transparent d-flex align-items-center gap-2 flex-shrink-0"
                @click="navigate_to_request_step(1)" :disabled="!can_navigate_to_step(1)">
                <span
                  :class="['rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold flex-shrink-0', current_request_step > 1 ? 'bg-success text-white' : 'bg-warning text-white']"
                  class="esigning-square-175">
                  <span v-if="current_request_step > 1">
                    <i class="bi bi-check-lg"></i>
                  </span>
                  <span v-else>1</span>
                </span>
                <span class="text-black">Document &amp; recipient</span>
              </button>

              <div class="flex-grow-1 border-top"></div>

              <button type="button" class="btn p-0 border-0 bg-transparent d-flex align-items-center gap-2 flex-shrink-0"
                @click="navigate_to_request_step(2)" :disabled="!can_navigate_to_step(2)">
                <span
                  :class="['rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold flex-shrink-0', current_request_step === 2 ? 'onboarding-bg-navy-dark text-white' : current_request_step > 2 ? 'bg-success text-white' : 'bg-light text-muted']"
                  class="esigning-square-175">
                  <span v-if="current_request_step > 2">
                    <i class="bi bi-check-lg"></i>
                  </span>
                  <span v-else>2</span>
                </span>
                <span :class="current_request_step >= 2 ? 'text-black' : 'text-muted'">Signers &amp; order</span>
              </button>

              <div class="flex-grow-1 border-top"></div>

              <button type="button" class="btn p-0 border-0 bg-transparent d-flex align-items-center gap-2 flex-shrink-0"
                @click="navigate_to_request_step(3)" :disabled="!can_navigate_to_step(3)">
                <span
                  :class="['rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold flex-shrink-0', current_request_step === 3 ? 'onboarding-bg-navy-dark text-white' : 'bg-light text-muted']"
                  class="esigning-square-175">3</span>
                <span :class="current_request_step === 3 ? 'text-black' : 'text-muted'">Review &amp; send</span>
              </button>
            </div>

            <button type="button"
              class="esigning-square-225 btn btn-sm bg-white border rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
              @click="navigate_to_request_step(current_request_step + 1)"
              :disabled="current_request_step >= 3 || !can_navigate_to_step(current_request_step + 1)">
              <i class="bi bi-chevron-right text-muted"></i>
            </button>
          </div>
        </div>
      </section>

      <section class="card border rounded-4 overflow-hidden">
        <div v-show="current_request_step === 1" class="p-4">
          <DocumentPreview
            :selected_template="selected_template"
            :template_bindings="template_bindings"
            :loading="template_bindings_loading"
            :load_error="template_bindings_error"
            @update:selected_template="selected_template = $event"
            @continue="go_to_signers_order"
          />
        </div>

        <div v-show="current_request_step === 2" class="p-4">
          <ConfigureSigners
            ref="configureSignersStep"
            :selected_template="selected_template"
            :entity_profile_id="route_entity_profile_id"
            :entity_type_id="route_entity_type_id"
            @busy-change="signers_step_busy = $event"
            @configured="handle_signers_configured"
          />
        </div>

        <div v-show="current_request_step === 3" class="p-4">
          <DocumentSubmission
            :selected_template="selected_template"
            :configured_signers="signer_configuration_summary"
            :populated_preview_url="populated_preview_url"
            :populated_preview_external_url="populated_preview_external_url"
            :populated_preview_form_src="populated_preview_form_src"
            :populated_preview_token="populated_preview_token"
            :populated_preview_loading="populated_preview_loading"
            :populated_preview_error="populated_preview_error"
          />
        </div>

        <div class="px-4 py-3">
          <div class="d-flex align-items-center justify-content-end gap-2">
            <button v-if="current_request_step === 2" type="button" class="btn btn-light border px-4"
              @click="go_to_document_preview" :disabled="signers_step_busy">
              Back
            </button>
            <button v-else-if="current_request_step === 3" type="button" class="btn btn-light border px-4"
              @click="go_to_signers_from_review" :disabled="sending_contract">
              Back
            </button>
            <button v-if="current_request_step === 2" type="button"
              class="btn onboarding-bg-navy-dark text-white fw-semibold px-4" @click="handle_signers_continue"
              :disabled="signers_step_busy">
              <span v-if="signers_step_busy">Saving...</span>
              <span v-else>Continue</span>
            </button>
            <button v-else-if="current_request_step === 3" type="button"
              class="btn onboarding-bg-navy-dark text-white fw-semibold px-4 d-inline-flex align-items-center gap-2"
              @click="handle_send_contract"
              :disabled="sending_contract || !ready_to_send_contract">
              <i class="bi bi-send-fill"></i>
              <span v-if="sending_contract">Sending...</span>
              <span v-else>Send for signing</span>
            </button>
          </div>
        </div>
      </section>
          </div>
        </section>
        </div>
      </div>
    </section>
  </div>
</main>
